import React, { useEffect, useState } from "react";
import Swal from "sweetalert2";
import {
  createRejection,
  fetchRejectionReasons,
  fetchTodayRejectionSummary,
} from "../../api/RejectionsApi";
import { fetchGHSEs } from "../../api/ghseApi";
import { fetchVarieties } from "../../api/varietyApi";
import { fetchHarvestTypes } from "../../api/harvestTypeApi"; // NEW
import "../../styles/RejectionsPage.css";

// ========================= REJECTION REASONS =========================


// ========================= STAT CARD COMPONENT =========================
const StatCard = ({ icon, label, value, color }) => (
  <div className="stat-card" style={{ "--accent": color }}>
    <div className="stat-icon" style={{ background: `${color}15`, color }}>
      {icon}
    </div>
    <div className="stat-info">
      <span className="stat-value">{value}</span>
      <span className="stat-label">{label}</span>
    </div>
  </div>
);

const RejectionsPage = () => {
  const [reasons, setReasons] = useState([]);
  const [summary, setSummary] = useState([]);
  const [ghses, setGhses] = useState([]);
  const [filteredGHSEs, setFilteredGHSEs] = useState([]);
  const [varieties, setVarieties] = useState([]);
  const [filteredVarieties, setFilteredVarieties] = useState([]);
  const [harvestTypes, setHarvestTypes] = useState([]); // NEW
  const [currentView, setCurrentView] = useState("selector");
  const [selectedFarm, setSelectedFarm] = useState("");

  const farms = [
    { id: "RVR 1", name: "RVR 1" },
    { id: "RVR 2", name: "RVR 2" },
  ];

  const [form, setForm] = useState({
    farm: "",
    ghse: "",
    variety: "",
    harvest_type: "", // NEW
    reason: "",
    rejected_stems: "",
    comment: "",
  });

  // ========================= LOAD DATA =========================
  const loadData = async () => {
    try {
      const [sumData, ghseData, varietyData, harvestData, reasonData] = await Promise.all([
        fetchTodayRejectionSummary(),
        fetchGHSEs(),
        fetchVarieties(),
        fetchHarvestTypes(), // NEW
        fetchRejectionReasons(),
      ]);
      setReasons(reasonData || []);
      setSummary(sumData || []);
      setGhses(ghseData || []);
      setVarieties(varietyData || []);
      setHarvestTypes(harvestData || []); // NEW
    } catch {
      Swal.fire("Error", "Failed to load data", "error");
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  // ========================= FILTER GHSE BY FARM =========================
  useEffect(() => {
    if (!form.farm) {
      setFilteredGHSEs([]);
      return;
    }
    setFilteredGHSEs(ghses.filter((g) => g.farm_name === form.farm));
  }, [form.farm, ghses]);

  // ========================= FILTER VARIETIES BY FARM =========================
  useEffect(() => {
    if (!form.farm) {
      setFilteredVarieties([]);
      return;
    }
    const filtered = varieties.filter((v) =>
      v.locations_detail?.some((loc) => loc.farm_name === form.farm)
    );
    setFilteredVarieties(filtered);
  }, [form.farm, varieties]);

  // ========================= HANDLE CHANGE =========================
  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({
      ...prev,
      [name]: value,
      ...(name === "farm" && { ghse: "", variety: "", harvest_type: "" }),
      ...(name === "ghse" && { variety: "", harvest_type: "" }),
    }));
  };

  // ========================= FARM SELECTOR =========================
  const handleFarmSelect = (farm) => {
    setSelectedFarm(farm);
    setForm((prev) => ({ ...prev, farm }));
    setCurrentView("dashboard");
    loadData();
  };

  const handleBackToSelector = async () => {
    const result = await Swal.fire({
      title: "Change Farm?",
      text: `You are currently on ${selectedFarm}. Switch to a different farm?`,
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Yes, Change",
      cancelButtonText: "Stay Here",
      confirmButtonColor: "#a92f6f",
      cancelButtonColor: "#9e9a93",
    });
    if (result.isConfirmed) {
      setSelectedFarm("");
      setCurrentView("selector");
      setForm({
        farm: "",
        ghse: "",
        variety: "",
        harvest_type: "",
        reason: "",
        rejected_stems: "",
        comment: "",
      });
    }
  };

  // ========================= VALIDATION =========================
  const validateForm = () => {
    if (!form.farm || !form.ghse || !form.variety || !form.harvest_type || !form.reason || !form.rejected_stems) {
      return "Please fill all required fields before saving";
    }
    if (Number(form.rejected_stems) <= 0) {
      return "Rejected stems must be greater than 0";
    }
    return null;
  };

  // ========================= CONFIRMATION POPUP =========================
  const showConfirmation = async () => {
    const ghse = filteredGHSEs.find((g) => String(g.id) === String(form.ghse)) || {};
    const variety = filteredVarieties.find((v) => String(v.id) === String(form.variety)) || {};
    const harvestType = harvestTypes.find((h) => String(h.id) === String(form.harvest_type)) || {};

    const result = await Swal.fire({
      title: "Confirm Rejection",
      width: 520,
      html: `
        <div class="confirm-popup">
          <div class="confirm-header">
            <div class="confirm-icon reject">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <path d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z"/>
              </svg>
            </div>
            <p class="confirm-subtitle">Review rejection details before submitting</p>
          </div>
          <div class="confirm-details">
            <div class="confirm-row">
              <span class="confirm-label">Farm</span>
              <span class="confirm-value">${selectedFarm}</span>
            </div>
            <div class="confirm-row">
              <span class="confirm-label">GHSE</span>
              <span class="confirm-value">${ghse.name || "—"}</span>
            </div>
            <div class="confirm-row">
              <span class="confirm-label">Variety</span>
              <span class="confirm-value">${variety.name || "—"}</span>
            </div>
            <div class="confirm-row">
              <span class="confirm-label">Harvest Type</span>
              <span class="confirm-value">${harvestType.name || "—"}</span>
            </div>
            <div class="confirm-row">
              <span class="confirm-label">Reason</span>
              <span class="confirm-value">${form.reason}</span>
            </div>
            <div class="confirm-divider"></div>
            <div class="confirm-row highlight">
              <span class="confirm-label">Rejected Stems</span>
              <span class="confirm-value big">${form.rejected_stems}</span>
            </div>
            ${form.comment ? `
            <div class="confirm-row">
              <span class="confirm-label">Comment</span>
              <span class="confirm-value">${form.comment}</span>
            </div>
            ` : ""}
          </div>
        </div>
      `,
      showCancelButton: true,
      confirmButtonText: "Submit Rejection",
      cancelButtonText: "Edit Details",
      confirmButtonColor: "#a92f6f",
      cancelButtonColor: "#9e9a93",
      customClass: { popup: "reject-swal-popup confirm-swal" },
    });
    return result.isConfirmed;
  };

  // ========================= SUBMIT =========================
  const handleSubmit = async (e) => {
    e.preventDefault();

    const error = validateForm();
    if (error) {
      Swal.fire("Validation", error, "warning");
      return;
    }

    const confirmed = await showConfirmation();
    if (!confirmed) return;

    try {
      await createRejection({
        farm: form.farm,
        ghse: form.ghse,
        variety: form.variety,
        harvest_type: form.harvest_type, // NEW
        reason: form.reason,
        rejected_stems: Number(form.rejected_stems),
        comment: form.comment || "",
      });

      await Swal.fire({
        title: "Recorded!",
        text: "Rejection has been successfully logged.",
        icon: "success",
        confirmButtonColor: "#a92f6f",
        timer: 2000,
        timerProgressBar: true,
      });

      setForm({
        farm: selectedFarm,
        ghse: "",
        variety: "",
        harvest_type: "",
        reason: "",
        rejected_stems: "",
        comment: "",
      });
      loadData();
    } catch {
      Swal.fire("Error", "Failed to save rejection", "error");
    }
  };

  // ========================= STATS =========================
  const todayTotal = summary.reduce((acc, s) => acc + (s.total_rejected || 0), 0);
  const uniqueReasons = [...new Set(summary.map((s) => s.reason))].length;
  const topReason = summary.length > 0
    ? summary.reduce((prev, curr) => (prev.total_rejected > curr.total_rejected ? prev : curr)).reason
    : "—";

  // ========================= FARM SELECTOR VIEW =========================
  if (currentView === "selector") {
    return (
      <div className="rejections-selector-page">
        <div className="selector-bg-pattern">
          <div className="pattern-dot" style={{ top: "10%", left: "15%", width: "300px", height: "300px" }}></div>
          <div className="pattern-dot olive" style={{ top: "60%", right: "10%", width: "250px", height: "250px" }}></div>
          <div className="pattern-dot" style={{ bottom: "10%", left: "40%", width: "200px", height: "200px" }}></div>
        </div>

        <div className="rejections-selector-content">
          <div className="selector-header">
            <div className="selector-icon rejections-icon">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                <path d="M9 13h6m-3-3v6m-9 1V7a2 2 0 012-2h6l2 2h6a2 2 0 012 2v8a2 2 0 01-2 2H5a2 2 0 01-2-2z" />
              </svg>
            </div>
            <h1>Rejections Capture</h1>
            <p>Select a farm to record and track harvest rejections</p>
          </div>

          <div className="farm-cards-grid">
            {farms.map((farm) => (
              <button
                key={farm.id}
                className="farm-card-btn rejections-farm-card"
                onClick={() => handleFarmSelect(farm.name)}
              >
                <div className="farm-card-visual">
                  <div className="farm-illustration rejections-illustration">
                    <svg viewBox="0 0 64 64" fill="none">
                      <rect x="8" y="20" width="48" height="36" rx="3" fill="currentColor" opacity="0.12" />
                      <path d="M8 28L32 12l24 16" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
                      <rect x="22" y="36" width="20" height="20" rx="2" fill="currentColor" opacity="0.25" />
                      <path d="M28 42h8M32 38v8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                      <circle cx="48" cy="28" r="3" fill="currentColor" opacity="0.4" />
                    </svg>
                  </div>
                </div>
                <div className="farm-card-info">
                  <h3>{farm.name}</h3>
                  <span className="farm-status-badge">Active</span>
                </div>
                <div className="farm-card-arrow">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M9 18l6-6-6-6" />
                  </svg>
                </div>
              </button>
            ))}
          </div>

          <div className="selector-footer">
            <span className="brand-mark">Rejections Management System</span>
          </div>
        </div>
      </div>
    );
  }

  // ========================= DASHBOARD VIEW =========================
  const filteredSummary = summary.filter((s) => s.farm__name === selectedFarm);

  return (
    <div className="rejections-dashboard">
      {/* NAVIGATION */}
      <nav className="dashboard-nav rejections-nav">
        <div className="nav-brand">
          <div className="nav-logo rejections-logo">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M9 13h6m-3-3v6m-9 1V7a2 2 0 012-2h6l2 2h6a2 2 0 012 2v8a2 2 0 01-2 2H5a2 2 0 01-2-2z" />
            </svg>
          </div>
          <span className="nav-title">Rejections</span>
        </div>
        <div className="nav-farm-badge">
          <span className="farm-indicator rejections-indicator"></span>
          {selectedFarm}
        </div>
        <button className="nav-back-btn" onClick={handleBackToSelector}>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M15 19l-7-7 7-7" />
          </svg>
          Change Farm
        </button>
      </nav>

      <div className="dashboard-content rejections-content">
        {/* STATS ROW */}
        <div className="stats-row">
          <StatCard
            icon={<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>}
            label="Today's Total"
            value={todayTotal}
            color="#a92f6f"
          />
          <StatCard
            icon={<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" /></svg>}
            label="Unique Reasons"
            value={uniqueReasons}
            color="#a9ae50"
          />
          <StatCard
            icon={<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" /></svg>}
            label="Top Reason"
            value={topReason}
            color="#a92f6f"
          />
        </div>

        {/* CAPTURE FORM SECTION */}
        <section className="dash-section rejections-section">
          <div className="section-header">
            <div className="section-icon rejections-section-icon">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M12 4v16m-8-8h16" />
              </svg>
            </div>
            <div>
              <h2>Record Rejection</h2>
              <p>Log rejected harvest details for quality tracking</p>
            </div>
          </div>

          <div className="card rejections-form-card">
            <form onSubmit={handleSubmit} className="rejections-form">
              <div className="form-row">
                <div className="form-group">
                  <label>Farm</label>
                  <div className="input-with-suffix readonly">
                    <input type="text" value={selectedFarm} readOnly />
                    <span className="input-suffix">locked</span>
                  </div>
                </div>

                <div className="form-group">
                  <label>GHSE <span className="required">*</span></label>
                  <select name="ghse" value={form.ghse} onChange={handleChange} required>
                    <option value="">Select GHSE...</option>
                    {filteredGHSEs.map((g) => (
                      <option key={g.id} value={g.id}>{g.name}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Variety <span className="required">*</span></label>
                  <select name="variety" value={form.variety} onChange={handleChange} required>
                    <option value="">Select Variety...</option>
                    {filteredVarieties.map((v) => (
                      <option key={v.id} value={v.id}>{v.name}</option>
                    ))}
                  </select>
                </div>

                <div className="form-group">
                  <label>Harvest Type <span className="required">*</span></label>
                  <select name="harvest_type" value={form.harvest_type} onChange={handleChange} required>
                    <option value="">Select Harvest Type...</option>
                    {harvestTypes.map((h) => (
                      <option key={h.id} value={h.id}>{h.name}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Rejected Stems <span className="required">*</span></label>
                  <div className="input-with-suffix">
                    <input
                      type="number"
                      name="rejected_stems"
                      value={form.rejected_stems}
                      onChange={handleChange}
                      placeholder="0"
                      min="1"
                      required
                    />
                    <span className="input-suffix">stems</span>
                  </div>
                </div>

                <div className="form-group">
                  <label>Reason <span className="required">*</span></label>
                  <select name="reason" value={form.reason} onChange={handleChange} required>
                    <option value="">Select rejection reason...</option>
                    {reasons.map((r) => (
                      <option key={r.id} value={r.name}>{r.name}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="form-group">
                <label>Comment <span className="optional">(optional)</span></label>
                <textarea
                  name="comment"
                  value={form.comment}
                  onChange={handleChange}
                  placeholder="Additional details about the rejection..."
                  rows={3}
                />
              </div>

              <button type="submit" className="btn-primary rejections-btn">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                  <path d="M12 4v16m-8-8h16" />
                </svg>
                Record Rejection
              </button>
            </form>
          </div>
        </section>

        {/* TODAY'S SUMMARY SECTION */}
        <section className="dash-section summary-section">
          <div className="section-header">
            <div className="section-icon summary-section-icon">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
            </div>
            <div>
              <h2>Today's Summary</h2>
              <p>Overview of all rejections recorded today</p>
            </div>
            <span className="summary-count">{filteredSummary.length} entries</span>
          </div>

          <div className="card summary-card">
            {filteredSummary.length === 0 ? (
              <div className="empty-state rejections-empty">
                <div className="empty-icon">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                    <path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <h3>No Rejections Today</h3>
                <p>All clear for {selectedFarm}. Use the form above to record any rejections.</p>
              </div>
            ) : (
              <div className="summary-table-wrapper">
                <table className="rejections-table">
                  <thead>
                    <tr>
                      <th>Farm</th>
                      <th>GHSE</th>
                      <th>Variety</th>
                      <th>Harvest Type</th>
                      <th>Reason</th>
                      <th className="numeric">Total Stems</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredSummary.map((s, i) => (
                      <tr key={i}>
                        <td><span className="cell-farm">{s.farm__name}</span></td>
                        <td>{s.ghse__name}</td>
                        <td>{s.variety__name}</td>
                        <td>{s.harvest_type__name || "—"}</td>
                        <td><span className="cell-reason">{s.reason}</span></td>
                        <td className="numeric"><span className="cell-total">{s.total_rejected}</span></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </section>
      </div>
    </div>
  );
};

export default RejectionsPage;